import React from 'react';
import qs from 'qs';
import _ from 'lodash';

import { Box } from '@mui/material';

import { makeStyles } from '@mui/styles';
import { flattenQuery } from '../../modals/filters/filter.helpers';
import { groupChips } from '../../modals/filters/chip-grouping';
import { LISTING_PILL_RADIUS } from '../listing-pill';
import {
  parseFilterModalQuery,
  withSplitMultiSelectValues,
} from '../../../utils/query.utils';

/** Conditions as they compare against the url, which carries no ids. */
const withoutIds = (conditions: any[] = []) =>
  conditions.map(({ id, ...condition }: any) => condition);

export const TableFilterChips = ({ filterProps, routeProps, config }: any) => {
  const { chips = [] } = filterProps;
  const { q } = routeProps;

  // parse query from url and remove id for deep-equality check against chip-filters
  let parsedQuery: any = [];
  if (q.f) {
    parsedQuery = withoutIds(parseFilterModalQuery(q));
  }

  // check to make sure table chips utilize the valid naming convention
  const validChips = chips.filter((chip: any) => {
    const split = chip.filter.split('&');
    return split.every((filter: any) => filter.startsWith('f'));
  });

  // overwrite chips' url-encoded query-string with parsed query object
  const parsedChips = validChips.map((chip: any) => ({
    ...chip,
    filter: parseFilterModalQuery(qs.parse(chip.filter)),
  }));

  // Chips sharing a column are alternatives to each other -- on the quotes listing "New Business",
  // "Endorsements" and "Renewals" all answer one question -- so they render as one segmented
  // control and picking one drops the others. Chips on unrelated columns stay separate pills that
  // combine. The rule is the one the manage panel heads its groups with, so the two cannot drift.
  const groups = groupChips(parsedChips, (chip: any) =>
    chip.filter.map((condition: any) => condition.column)
  );

  return (
    <>
      {groups.map((group) => (
        <ChipGroup
          key={group.key}
          chips={group.chips}
          query={parsedQuery}
          config={config}
          {...routeProps}
        />
      ))}
    </>
  );
};

/**
 * One set of alternatives, as a single segmented control.
 *
 * <p>A group of one is the same markup and reads as an ordinary pill, so a listing whose chips
 * share nothing looks exactly as it always did.
 */
const ChipGroup = ({ chips, ...rest }: any) => {
  const classes = useChipStyles();

  return (
    <Box className={classes.group} role="group">
      {chips.map((chip: any, index: number) => (
        <FilterChip
          key={chip.value}
          chip={chip}
          siblings={chips.filter((other: any) => other.value !== chip.value)}
          first={index === 0}
          {...rest}
        />
      ))}
    </Box>
  );
};

const FilterChip = ({
  chip,
  siblings,
  first,
  query,
  q,
  config,
  ...routeProps
}: any) => {
  const { location } = routeProps;
  const classes = useChipStyles();

  // delete filter ids for deep-equality check against query
  const chipConditions = withoutIds(chip.filter);

  // derive initial selected state by comparing chip-filters to the current url
  const selected = chipConditions.every((f: any) => {
    return !_.isEmpty(query.filter((el: any) => _.isMatch(el, f)));
  });

  const withoutConditions = (from: any[], conditions: any[]) =>
    conditions.reduce(
      (acc: any[], condition: any) =>
        acc.filter((el) => !_.isMatch(el, condition)),
      from
    );

  // toggle filter and update url onClick
  const handleSelect = () => {
    const { f, fn, page, searchAfter, pageRequested, ...rest } = q;

    // Selecting clears the rest of the group first. Two chips in one group ask the same question
    // two ways, and leaving the previous one applied narrows to the intersection -- usually
    // nothing -- which reads as a chip that silently emptied the table.
    let nextQ = withoutConditions(query, chipConditions);
    if (!selected) {
      siblings.forEach((sibling: any) => {
        nextQ = withoutConditions(nextQ, withoutIds(sibling.filter));
      });
      nextQ = [...nextQ, ...chipConditions];
    }

    // Split before flattening, as the modal's own doors do. A chip stores a multi-value column
    // as the comma-joined string parseFilterModalQuery hands back -- it splits on the operator,
    // and `sw` is not one of the operators it splits -- so stringifyQuery would encode
    // "PLM-S,CIC-S" whole and the listing would match nothing. Without a config this is a no-op,
    // which is every listing that has no multi-value column.
    const filterParams = flattenQuery(
      withSplitMultiSelectValues(nextQ, config)
    );
    routeProps.replace(location.pathname, {
      query: { ...rest, ...filterParams },
    });
  };

  return (
    // A real button, not a div wearing `role="button"`. The role and a tabIndex announce it to a
    // screen reader as something to press, and then Enter and Space did nothing, because a div has
    // no activation behaviour to inherit. The element brings the role, the tab stop and keyboard
    // activation with it.
    <Box
      component="button"
      type="button"
      className={`${classes.segment} ${first ? '' : classes.divided} ${
        selected ? classes.segmentSelected : ''
      }`}
      onClick={handleSelect}
      aria-pressed={selected}
      data-qa={`TABLE_FILTER_CHIP:${chip.label}`}
    >
      {chip.label}
    </Box>
  );
};

const useChipStyles = makeStyles(({ config, palette }) => ({
  /* The track a set of alternatives shares. One border round the set rather than one per chip is
     what says "pick one of these" -- the same thing a segmented control says. */
  group: {
    display: 'inline-flex',
    alignItems: 'stretch',
    margin: '0.3rem 1.2rem 0.3rem 0',
    borderRadius: LISTING_PILL_RADIUS,
    border: `1px solid ${config.colors.cowbellBlue}`,
    overflow: 'hidden',
  },
  segment: {
    padding: '0.4rem 0.8rem',
    cursor: 'pointer',
    color: palette.text.secondary,
    whiteSpace: 'nowrap',
    // The group draws the border and the background; the button element brings its own of both,
    // plus a font that is not the page's.
    background: 'none',
    border: 0,
    font: 'inherit',
  },
  /* Hairlines stay 1px -- scaling one renders blurry. */
  divided: {
    borderLeft: `1px solid ${config.colors.cowbellBlue}`,
  },
  segmentSelected: {
    backgroundColor: config.colors.cowbellBlue,
    color: 'white',
  },
}));
